import 'keen-slider/keen-slider.min.css';

/**
 * ProgressiveImageCarousel
 *
 * This component diverged from SwipeGallery to implement a more efficient image loading strategy:
 * - Loads only the first image initially so that we do not affect the page load performance
 * - Preloads the second image on hover to reduce perceived loading time
 * - Loads subsequent images only when the active image is loaded
 *
 * - Added as part of fbdb929e-image-carousel-desktop-cards
 */
import { useCallback, useContext, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { styled } from '@linaria/react';
import { HotelCardContext } from 'components/HotelCardContext';
import { useKeenSlider } from 'keen-slider/react';
import { getParametrizedHotelContext, getParametrizedHotelInfoContext } from 'middleware/analytics/selectors';
import { getIsRtlLanguage } from 'modules/meta/selectors';
import { Button } from '@daedalus/atlas/Button';
import { CurrentPaneIndicators } from '@daedalus/atlas/components/designSystem/helpers/SwipePanes/CurrentPaneIndicators';
import { Box } from '@daedalus/atlas/helpers/Box';
import useIsImageloaded from '@daedalus/atlas/hooks/useIsImageloaded';
import { Icon } from '@daedalus/atlas/Icon';
import { SIZES } from '@daedalus/atlas/Image';
import { SkeletonLoader } from '@daedalus/atlas/SkeletonLoader';
import { getDevicePixelRatio } from '@daedalus/core/src/_web/utils/browser/getDevicePixelRatio';
import { trackEvent } from '@daedalus/core/src/analytics/modules/actions';
import { Action, AnalyticsContext, Category, Entity } from '@daedalus/core/src/analytics/types/Events';
import { ImageProvider } from '@daedalus/core/src/staticContent/types/ImageProvider';
const _exp = /*#__PURE__*/() => Button;
const ArrowButton = /*#__PURE__*/styled(_exp())({
  name: "ArrowButton",
  class: "a5p99dv",
  propsAsIs: true
});
const _exp4 = /*#__PURE__*/() => ({
  isLoaded,
  isPriority
}) => isPriority ? 1 : isLoaded ? 1 : 0;
const _exp5 = /*#__PURE__*/() => ({
  isPriority
}) => isPriority ? 'none' : 'opacity 0.3s ease-in-out';
const FadeInImage = /*#__PURE__*/styled('div')({
  name: "FadeInImage",
  class: "fvodxbc",
  propsAsIs: false,
  vars: {
    "fvodxbc-0": [_exp4()],
    "fvodxbc-1": [_exp5()]
  }
});
interface Props {
  images: string[];
  imageProvider: ImageProvider;
  isPriorityCard?: boolean;
}
interface SlideImageWithSkeletonProps {
  src: string;
  loading: 'eager' | 'lazy';
  processImageUrl: (url: string) => string;
  isPriorityImage?: boolean;
}

// Component that combines image (img) with skeleton loader
const SlideImageWithSkeleton: React.FC<SlideImageWithSkeletonProps> = ({
  src,
  loading,
  processImageUrl,
  isPriorityImage = false
}) => {
  const processedSrc = processImageUrl(src);
  const {
    isLoaded,
    isErrored
  } = useIsImageloaded(processedSrc);
  return <>
      <FadeInImage isLoaded={isLoaded} isPriority={isPriorityImage}>
        {/* eslint-disable-next-line @next/next/no-img-element */}
        <img src={processedSrc} loading={loading}
      // eslint-disable-next-line react/no-unknown-property
      fetchPriority={isPriorityImage ? 'high' : 'auto'} alt="" />
      </FadeInImage>
      {!isLoaded && !isErrored && <Box position="absolute" top="0" left="0" width="100%" height="100%" zIndex="1">
          <SkeletonLoader />
        </Box>}
    </>;
};
const CONTAINER_WIDTH = 278;
export const ProgressiveImageCarousel: React.FC<Props> = ({
  images,
  imageProvider,
  isPriorityCard
}) => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [loaded, setLoaded] = useState(false);
  const [loadedImages, setLoadedImages] = useState<number[]>([0]); // Initially load just the first image

  const dispatch = useDispatch();
  const {
    hotelId
  } = useContext(HotelCardContext);
  const isRtl = useSelector(getIsRtlLanguage);
  const analyticsContext = {
    [AnalyticsContext.HotelContext]: getParametrizedHotelContext(hotelId),
    [AnalyticsContext.HotelInfoContext]: getParametrizedHotelInfoContext(hotelId)
  };
  const dpr = getDevicePixelRatio(window);

  // Track which images we're currently loading to avoid duplicate loading attempts
  const loadingImagesRef = useRef<Set<number>>(new Set());

  // Process image URLs through the imageProvider
  const getProcessedImageUrl = useCallback((url: string) => {
    if (!imageProvider || !url) return url;
    try {
      const extraProcessingParams = {
        dpr
      };
      return imageProvider(url, SIZES['extraLarge'], extraProcessingParams);
    } catch (e) {
      return url;
    }
  }, [imageProvider, dpr]);

  // Function to preload an image
  const preloadImage = useCallback((url: string) => {
    const img = new Image();
    img.src = getProcessedImageUrl(url);
  }, [getProcessedImageUrl]);

  // Function to load a specific image
  const loadImage = useCallback((index: number) => {
    // Skip if already loaded or currently loading
    if (loadedImages.includes(index) || loadingImagesRef.current.has(index)) {
      return;
    }

    // Mark as loading
    loadingImagesRef.current.add(index);

    // Preload the actual image
    if (images[index]) {
      preloadImage(images[index]);
    }

    // Update state to indicate this image is loaded
    setLoadedImages(prev => [...prev, index]);
  }, [loadedImages, images, preloadImage]);

  // Initialize the slider with consistent slide sizing
  const [sliderRef, instanceRef] = useKeenSlider({
    initial: 0,
    slides: {
      perView: 1,
      spacing: 0
    },
    defaultAnimation: {
      duration: 800
    },
    renderMode: 'performance',
    drag: false,
    rtl: isRtl,
    slideChanged(slider) {
      const newSlide = slider.track.details.rel;
      setCurrentSlide(newSlide);

      // Load current and next slide
      if (!loadedImages.includes(newSlide)) {
        loadImage(newSlide);
      }
      const nextSlide = newSlide + 1;
      if (nextSlide < images.length && !loadedImages.includes(nextSlide)) {
        loadImage(nextSlide);
      }
      dispatch(trackEvent({
        category: Category.User,
        entity: Entity.HotelCardImageCarousel,
        action: Action.Clicked,
        analyticsContext
      }));
    },
    created() {
      setLoaded(true);
    }
  });

  // Handle hover to load the second image
  const handleMouseEnter = useCallback(() => {
    if (images.length > 1 && !loadedImages.includes(1)) {
      loadImage(1);
    }
  }, [images.length, loadedImages, loadImage]);
  return <Box position="relative" width="100%" height="100%" onMouseEnter={handleMouseEnter} className="gallery-container">
      <div ref={sliderRef} className="keen-slider" style={{
      height: '100%'
    }}>
        {images.map((src, idx) => <div key={idx} className="keen-slider__slide">
            <Box height="100%" width="100%" display="flex" alignItems="center" justifyContent="center" overflow="hidden" position="relative">
              {loadedImages.includes(idx) && <SlideImageWithSkeleton src={src} loading={idx === 0 ? 'eager' : 'lazy'} processImageUrl={getProcessedImageUrl} isPriorityImage={isPriorityCard && idx === 0} />}
            </Box>
          </div>)}
      </div>

      {/* Navigation arrows - visible on container hover */}
      {loaded && images.length > 1 && instanceRef.current && <>
          <ArrowButton dataId="paginationLeftButton" iconStart={<Icon name="ChevronLeft" />} size="sm" variant="secondary" isFloating isRounded onClick={e => {
        e.stopPropagation();
        instanceRef.current?.prev();
      }} className={`prev ${currentSlide === 0 ? 'hidden' : ''}`} />

          <ArrowButton dataId="paginationRightButton" iconStart={<Icon name="ChevronRight" />} size="sm" variant="secondary" isFloating isRounded onClick={e => {
        e.stopPropagation();
        instanceRef.current?.next();
      }} className={`next ${currentSlide === instanceRef.current.track.details.slides.length - 1 ? 'hidden' : ''}`} />

          <CurrentPaneIndicators amount={images.length} currentIndex={currentSlide} width={CONTAINER_WIDTH} hasDarkGradient={false} isRtl={isRtl} />
        </>}
    </Box>;
};

require("./ProgressiveImageCarousel.wyw-in-js.css!=!../../../../../node_modules/@wyw-in-js/webpack-loader/lib/outputCssLoader.js?cacheProvider=!./ProgressiveImageCarousel.tsx");